import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { apiFetch } from "../lib/api";
import { Logo } from "../components/Logo";
import type {
  AcceptInviteResponse,
  InvitePreviewResponse,
} from "@family/shared";

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const { state } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const preview = useQuery<InvitePreviewResponse>({
    queryKey: ["invite-preview", token],
    queryFn: () =>
      apiFetch<InvitePreviewResponse>(`/invites/${token}/preview`),
    enabled: Boolean(token),
    retry: false,
  });

  const accept = useMutation<AcceptInviteResponse>({
    mutationFn: () =>
      apiFetch<AcceptInviteResponse>(`/invites/${token}/accept`, {
        method: "POST",
      }),
    onSuccess: () => {
      // Invalidate /me so the new membership shows up immediately.
      queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/", { replace: true });
    },
  });

  const signedIn = state.status === "signed-in";
  const loginHref = `/login?from=${encodeURIComponent(`/invite/${token ?? ""}`)}`;

  return (
    <div className="page--auth">
      <div className="page--auth__card stack">
        <div className="page--auth__logo">
          <Logo variant="on-light" size={120} />
        </div>

        <h1 style={{ textAlign: "center" }}>You're invited</h1>

        {preview.isLoading && <p className="text-muted">Looking up invite…</p>}

        {preview.error && (
          <p className="text-error">
            This invite link is invalid or no longer exists.
          </p>
        )}

        {preview.data && (
          <>
            <p style={{ textAlign: "center" }} className="text-muted">
              You've been invited to join <strong>{preview.data.familyName}</strong>{" "}
              as <strong>{preview.data.role}</strong>.
            </p>

            {preview.data.consumed && (
              <p className="text-error">This invite has already been used.</p>
            )}
            {preview.data.expired && !preview.data.consumed && (
              <p className="text-error">This invite has expired.</p>
            )}

            {!preview.data.consumed && !preview.data.expired && (
              <>
                {signedIn ? (
                  <button
                    onClick={() => accept.mutate()}
                    disabled={accept.isPending}
                  >
                    {accept.isPending ? "Joining…" : "Accept invite"}
                  </button>
                ) : (
                  <Link to={loginHref} style={{ textDecoration: "none" }}>
                    <button style={{ width: "100%" }}>
                      Sign in to accept
                    </button>
                  </Link>
                )}
                {accept.error && (
                  <p className="text-error">
                    {(accept.error as Error).message}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
